import { useCallback, useState, type ReactNode } from 'react'
import { WindowManagerContext, type WindowState, type WindowManagerContextType, type WindowOpenOpts } from './window-manager-context'

export type { WindowState, WindowManagerContextType, WindowOpenOpts }

const MAX_WINDOWS = 4
let nextZIndex = 100

function generateId(): string {
  return Math.random().toString(36).slice(2, 10)
}

// Cascade new window positions so they don't stack exactly
function cascadePosition(index: number): { x: number; y: number } {
  const offset = (index % 5) * 30
  return { x: 70 + offset, y: 60 + offset }
}

export function WindowManagerProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState<WindowState[]>([])
  const [maxReached, setMaxReached] = useState(false)

  const openWindow = useCallback((route: string, title: string, opts?: WindowOpenOpts): boolean => {
    let opened = false
    setWindows(prev => {
      // Deduplicate: focus existing window with same route
      const existing = prev.find(w => w.route === route)
      if (existing) {
        nextZIndex++
        opened = true
        return prev.map(w =>
          w.id === existing.id
            ? { ...w, minimized: false, zIndex: nextZIndex }
            : w,
        )
      }

      // Pinned windows don't count toward the limit
      const unpinned = prev.filter(w => !w.pinned)
      if (!opts?.pinned && unpinned.length >= MAX_WINDOWS) {
        setMaxReached(true)
        return prev
      }

      nextZIndex++
      const position = opts?.position ?? cascadePosition(prev.length)
      const newWindow: WindowState = {
        id: generateId(),
        title,
        route,
        position,
        size: opts?.size ?? { w: 874, h: 477 },
        minimized: false,
        maximized: false,
        zIndex: nextZIndex,
        pinned: opts?.pinned,
      }
      opened = true
      setMaxReached(false)
      return [...prev, newWindow]
    })
    return opened
  }, [])

  const closeWindow = useCallback((id: string) => {
    setWindows(prev => {
      // Pinned windows cannot be closed
      const target = prev.find(w => w.id === id)
      if (target?.pinned) return prev
      return prev.filter(w => w.id !== id)
    })
    setMaxReached(false)
  }, [])

  const focusWindow = useCallback((id: string) => {
    nextZIndex++
    setWindows(prev =>
      prev.map(w => w.id === id ? { ...w, zIndex: nextZIndex } : w),
    )
  }, [])

  const minimizeWindow = useCallback((id: string) => {
    setWindows(prev =>
      prev.map(w => w.id === id ? { ...w, minimized: true } : w),
    )
  }, [])

  const maximizeWindow = useCallback((id: string) => {
    nextZIndex++
    setWindows(prev =>
      prev.map(w => {
        if (w.id !== id) return w
        if (w.maximized) {
          // Restore
          return {
            ...w,
            maximized: false,
            position: w.prevBounds ? { x: w.prevBounds.x, y: w.prevBounds.y } : w.position,
            size: w.prevBounds ? { w: w.prevBounds.w, h: w.prevBounds.h } : w.size,
            zIndex: nextZIndex,
            prevBounds: undefined,
          }
        }
        return {
          ...w,
          maximized: true,
          prevBounds: { x: w.position.x, y: w.position.y, w: w.size.w, h: w.size.h },
          position: { x: 0, y: 0 },
          zIndex: nextZIndex,
        }
      }),
    )
  }, [])

  const restoreWindow = useCallback((id: string) => {
    nextZIndex++
    setWindows(prev =>
      prev.map(w => w.id === id ? { ...w, minimized: false, zIndex: nextZIndex } : w),
    )
  }, [])

  const moveWindow = useCallback((id: string, pos: { x: number; y: number }) => {
    setWindows(prev =>
      prev.map(w => w.id === id ? { ...w, position: pos } : w),
    )
  }, [])

  const resizeWindow = useCallback((id: string, size: { w: number; h: number }) => {
    setWindows(prev =>
      prev.map(w => w.id === id ? { ...w, size } : w),
    )
  }, [])

  const closeAll = useCallback(() => {
    setWindows(prev => prev.filter(w => w.pinned))
    setMaxReached(false)
  }, [])

  return (
    <WindowManagerContext.Provider
      value={{
        windows,
        openWindow,
        closeWindow,
        focusWindow,
        minimizeWindow,
        maximizeWindow,
        restoreWindow,
        moveWindow,
        resizeWindow,
        closeAll,
        maxReached,
      }}
    >
      {children}
    </WindowManagerContext.Provider>
  )
}
